/**
 * CookieConsent — GDPR-compliant cookie consent banner.
 * Shows on first visit, persists consent in localStorage.
 */

import React, { useState, useEffect } from 'react';
import { Cookie, X, Shield, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';

const CookieConsent: React.FC = () => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('bhealth_cookie_consent');
    if (!consent) setShow(true);
  }, []);

  const handleAccept = (level: 'all' | 'necessary') => {
    localStorage.setItem('bhealth_cookie_consent', JSON.stringify({
      level,
      timestamp: new Date().toISOString(),
      necessary: true,
      analytics: level === 'all',
      marketing: false,
    }));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-surface-200 p-5 sm:p-6 animate-slide-up">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <Cookie size={20} className="text-primary-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-text-primary mb-1">{t('cookieConsent.title')}</h3>
            <p className="text-sm text-text-secondary mb-3">
              {t('cookieConsent.description')}
            </p>

            {showDetails && (
              <div className="space-y-2 mb-4 text-xs text-text-muted">
                <div className="flex items-center justify-between p-2 bg-surface-50 rounded-lg">
                  <span>{t('cookieConsent.essentialCookies')}</span>
                  <span className="text-success-600 font-medium">{t('cookieConsent.alwaysActive')}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-surface-50 rounded-lg">
                  <span>{t('cookieConsent.analyticsCookies')}</span>
                  <span className="text-text-muted">{t('cookieConsent.optional')}</span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => handleAccept('all')} className="btn btn-primary btn-sm">
                {t('cookieConsent.acceptAll')}
              </button>
              <button onClick={() => handleAccept('necessary')} className="btn btn-outline btn-sm">
                {t('cookieConsent.necessaryOnly')}
              </button>
              <button onClick={() => setShowDetails(!showDetails)} className="btn btn-ghost btn-sm text-xs">
                <Settings size={14} /> {t('cookieConsent.details')}
              </button>
              <div className="flex items-center gap-2 ml-auto text-xs text-text-muted">
                <Link to="/privacy" className="hover:text-primary-500">{t('cookieConsent.privacyPolicy')}</Link>
                <span>·</span>
                <Link to="/terms" className="hover:text-primary-500">{t('cookieConsent.terms')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
